/*
 * @FileName       : index.ts
 * @Version        : 1.0
 * @DateOfCreation : 04/17/2020
 * @Author         : $u^^!t @ FFF Enterprises
 * @Dependencies   : N/A
 * @Description    : Root Saga to update all the saga effects in code.
 *
 * Copyright (c) 2020, FFF Enterprises. All rights reserved.
 */

import { takeLatest, throttle } from 'redux-saga/effects';
import {
	REPORTS,
	UI,
	CABINET,
	TENANTS,
	INVENTORY_MANAGEMENT,
	USER,
	USER_MANAGEMENT,
	INVOICES,
	ALERTS,
	PRODUCTS,
	ORDERS,
	SAVE_SEARCH_ACTIONS,
	AUDIT,
	DASHBOARD,
} from '../constants/actions';

import * as reports from './reports.saga';
import * as ui from './ui.saga';
import * as cabinets from './cabinets.saga';
import * as tenants from './tenants.saga';
import * as invoices from './invoices.saga';
import * as inventoryManagement from './inventory-management.saga';
import * as user from './user.saga';
import * as userManagement from './user-management.saga';
import * as alerts from './alerts.saga';
import * as product from './product.saga';
import * as orders from './orders.saga';
import * as audit from './audit.saga';
import * as dashboard from './dashboard.saga';

export default function* rootSaga() {
	// Saga for Reports Reducer
	yield takeLatest(REPORTS.INVENTORY.HISTORY.LIST, reports.getInventoryReport);
	yield takeLatest(REPORTS.INVENTORY.CURRENT.LIST, reports.getInventoryReport);
	yield takeLatest(REPORTS.INVENTORY.GROUP.REQUEST, reports.getInventoryGroup);
	yield takeLatest(REPORTS.INVENTORY.USAGE.LIST, reports.getRecentlyVended);
	yield takeLatest(REPORTS.INVENTORY.USAGE.EXPORT, reports.getTransactionLogCsvExport);
	yield takeLatest(REPORTS.INVENTORY.USAGE.GET_GROUP, reports.getTransactionLogGroup);

	yield takeLatest(REPORTS.RESTOCK_ORDER_DETAILS.IG_SHIPMENT_ITEMS, reports.getRestockOrderDetails_igShipmentItems);
	yield takeLatest(REPORTS.RESTOCK_ORDER_DETAILS.RESTOCK_REQUEST, reports.getRestockOrderDetails_restockRequest);
	yield takeLatest(REPORTS.RESTOCK_ORDER_DETAILS.WAREHOUSE_SHIPMENT, reports.getRestockOrderDetails_warehouseShipment);
	yield takeLatest(REPORTS.RESTOCK_ORDER_DETAILS.TRANSACTIONS, reports.getRestockOrderDetails_transactions);
	yield takeLatest(REPORTS.RESTOCK_ORDER_DETAILS.SAP_ORDERS, reports.getRestockOrderDetails_sapOrders);

	yield takeLatest(INVOICES.LIST, invoices.getInvoicesList);
	yield takeLatest(INVOICES.OPEN_PAYMENT_PAGE, invoices.openPaymentPage);
	yield takeLatest(INVOICES.PAYMENT_CONFIRMATION, invoices.paymentConfirmation);
	yield takeLatest(INVOICES.GET_REQUESTS, invoices.getInvoiceRequests);
	yield takeLatest(INVOICES.PATCH_REQUEST_ABW, invoices.patchInvoiceRequestAbwSelection);
	yield takeLatest(INVOICES.PATCH_REQUEST_OMITTAL, invoices.patchInvoiceRequestOmittal);
	yield takeLatest(INVOICES.PATCH_REQUEST_OMITTAL_BULK, invoices.patchInvoiceRequestOmittalBulk);
	yield takeLatest(INVOICES.SEND_ABW_REMINDER_EMAIL, invoices.sendAbwReminderEmail);
	yield takeLatest(INVOICES.TRANSMIT_REQUESTS_TO_SAP, invoices.transmitRequestsToSap);
	yield takeLatest(INVOICES.UPDATE_INVOICE_STATUS, invoices.patchInvoiceRequestStatus);
	yield takeLatest(INVOICES.PATCH_MAKE_ALL_INVOICES_READY, invoices.patchMakeAllInvoicesReady);

	// Saga for UI Reducer
	yield takeLatest(UI.NAVIGATE, ui.updateUINavigateState);
	yield takeLatest(UI.SET_INVENTORY_STATE, ui.updateInventoryFilterState);
	yield takeLatest(UI.SET_CUSTOMER, ui.setSelectedCustomer);

	// Saga for Cabinet Reducer
	yield throttle(500, CABINET.LIST, cabinets.getCabinets);
	yield takeLatest(CABINET.GET_CONTEXT_CABINETS, cabinets.getContextCabinets);
	yield takeLatest(CABINET.TABLE_LIST, cabinets.getCabinetsTable);
	yield takeLatest(CABINET.GET_CABINET_DETAILS_REQUEST, cabinets.getCabinetDetails);
	yield takeLatest(CABINET.GET_PRODUCTS, cabinets.getCabinetProducts);
	yield takeLatest(CABINET.UPDATE_PO_TYPE, cabinets.updatePoType);
	yield takeLatest(CABINET.UPDATE, cabinets.updateCabinet);
	yield takeLatest(CABINET.GET_CABINET_USERS, cabinets.getUsers);
	yield takeLatest(CABINET.GET_SINGLE_CABINET_USER, cabinets.getSingleCabinetUser);
	yield takeLatest(CABINET.GET_AVAILABLE_USERS, cabinets.getAvailableUsers);
	yield takeLatest(CABINET.ADD_CABINET_USER, cabinets.addCabinetUser);
	yield takeLatest(CABINET.UPDATE_CABINET_USER, cabinets.updateCabinetUser);
	yield takeLatest(CABINET.CREATE_CABINET, cabinets.createCabinet);
	yield takeLatest(CABINET.ADD_PRODUCT, cabinets.addCabinetProduct);
	yield takeLatest(CABINET.GET_CABINET_PRODUCT_INVENTORY, cabinets.getCabinetProductInventory);
	yield takeLatest(CABINET.GET_CONSIGNMENT_ORDERS, cabinets.getConsignmentOrders);
	yield takeLatest(CABINET.UPDATE_CABINET_PRODUCT_INVENTORY, cabinets.updateCabinetProductInventory);
	yield takeLatest(CABINET.REPORT_CABINET_INVENTORY, cabinets.reportCabinetInventory);
	yield takeLatest(CABINET.GET_CABINET_LATEST_SCAN, cabinets.getCabinetLatestScan);
	yield takeLatest(CABINET.GET_PROPERTY_LIST, cabinets.getPropertyList);
	yield takeLatest(CABINET.GET_NEXT_CABINET_ID, cabinets.getNextCabinetId);
	yield takeLatest(CABINET.IMPORT_DEVICE_SETTINGS, cabinets.importDeviceSettings);
	yield takeLatest(CABINET.GET_PROVIDER_BY_ID, cabinets.getProviderById);
	yield takeLatest(CABINET.ADD_CABINET_PROVIDER, cabinets.addCabinetProvider);
	yield takeLatest(CABINET.DELETE_CABINET_PROVIDERS, cabinets.deleteCabinetProviders);
	yield takeLatest(CABINET.ADD_CABINET_LOCATION_TRACKING, cabinets.addCabinetLocationTracking);
	yield takeLatest(CABINET.DELETE_CABINET_LOCATION_TRACKING, cabinets.deleteCabinetLocationTracking);
	yield takeLatest(CABINET.GET_CYCLE_COUNT_INVENTORY, cabinets.getCycleCountInventory);
	yield takeLatest(CABINET.GET_PROPERTIES_BY_CABINET_TYPE, cabinets.getPropertiesByCabinetType);
	yield takeLatest(CABINET.SAVE_CABINET_PROPERTIES, cabinets.saveCabinetProperties);
	yield takeLatest(CABINET.DELETE_CABINET_PROPERTY, cabinets.deleteCabinetProperty);
	yield takeLatest(CABINET.SAVE_CABINET_PROPERTY_META, cabinets.saveCabinetPropertyMeta);
	yield takeLatest(CABINET.SET_CABINET_PRODUCT_PAR, cabinets.setParLevels);
	yield takeLatest(CABINET.GET_FLEX_CONFIG, cabinets.getFlexConfig);
	yield takeLatest(CABINET.UPDATE_FLEX_CONFIG, cabinets.updateFlexConfig);
	yield takeLatest(CABINET.SEND_MANUAL_RECEIPT, cabinets.sendManualReceipt);
	yield takeLatest(CABINET.FLEX_RESET_DEFAULT, cabinets.flexResetDefault);
	yield takeLatest(CABINET.SEND_MANUAL_CORRECTION, cabinets.sendManualCorrection);
	yield takeLatest(CABINET.INVENTORY_SYNC, cabinets.sendInventorySync);
	yield takeLatest(CABINET.GET_DEVICE_TAGS, cabinets.getDeviceTags);
	yield takeLatest(CABINET.SET_DEVICE_TAGS, cabinets.setDeviceTags);
	yield takeLatest(CABINET.GET_CABINET_RECEIPTS, cabinets.getCabinetReceipts);
	yield takeLatest(CABINET.REJECT_CABINET_RECEIPT, cabinets.rejectCabinetReceipt);
	yield takeLatest(CABINET.GET_CABINET_CONNECTIVITY_GRAPH, cabinets.getCabinetConnectivity);
	yield takeLatest(CABINET.GET_CABINET_LTEWIFI_GRAPH, cabinets.getCabinetLteWifi);
	yield takeLatest(CABINET.GET_EDGE_CONFIG_PROPERTIES, cabinets.getEdgeConfigProperties);
	yield takeLatest(CABINET.UPDATE_EDGE_CONFIG_PROPERTIES, cabinets.updateEdgeConfigProperties);
	yield takeLatest(CABINET.GET_SECURE_DEVICE_COMPARTMENTS, cabinets.getSecureDeviceCompartments);

	yield throttle(500, TENANTS.LIST, tenants.getTenantsList);
	yield throttle(500, TENANTS.DETAILS.CREATE_CUSTOMER_PRODUCT, tenants.createCustomerProduct);
	yield throttle(500, TENANTS.DETAILS.NOTIFICATION_ACTIVITY_REQUEST, tenants.getNotificationActivity);
	yield throttle(500, TENANTS.DETAILS.NOTIFICATION_MESSAGE_REQUEST, tenants.getNotificationMessage);

	yield throttle(500, INVENTORY_MANAGEMENT.LIST_PAR, inventoryManagement.getParInventory);
	yield throttle(500, INVENTORY_MANAGEMENT.LIST_INVENTORY, inventoryManagement.getTenantInventory);
	yield takeLatest(INVENTORY_MANAGEMENT.CARTRIDGE_INVENTORY, inventoryManagement.getCartridgeInventory);
	yield takeLatest(INVENTORY_MANAGEMENT.SERIAL_INVENTORY, inventoryManagement.getSerialInventory);
	yield takeLatest(INVENTORY_MANAGEMENT.INVENTORY_CSV, inventoryManagement.getInventoryCsvExport);

	yield takeLatest(INVENTORY_MANAGEMENT.SET_QUANTITY, inventoryManagement.postInventoryUsage);
	yield takeLatest(INVENTORY_MANAGEMENT.UPDATE_RECEIPT, inventoryManagement.patchInventoryReceipt);

	yield takeLatest(INVENTORY_MANAGEMENT.GET_SUSPENDED_TRANSACTIONS, inventoryManagement.getSuspendedTransactions);
	yield takeLatest(INVENTORY_MANAGEMENT.UPDATE_SUSPENDED_TRANSACTIONS, inventoryManagement.updateSuspendedTransactions);

	yield takeLatest(INVENTORY_MANAGEMENT.GET_TAGS, inventoryManagement.getTags);
	yield takeLatest(INVENTORY_MANAGEMENT.GET_TAG_LIFECYCLE, inventoryManagement.getTagLifecycle);
	yield takeLatest(INVENTORY_MANAGEMENT.UPDATE_PRODUCT_TAG_SERIALS, inventoryManagement.updateProductTagSerials);
	yield takeLatest(INVENTORY_MANAGEMENT.FORCE_VEND, inventoryManagement.forceVend);
	yield takeLatest(INVENTORY_MANAGEMENT.REVERT_PRODUCT_TAG_SERIALS, inventoryManagement.revertTag);
	yield takeLatest(INVENTORY_MANAGEMENT.RETURN_PRODUCT_TAG_SERIALS, inventoryManagement.returnProductTagSerialsRecord);
	yield takeLatest(INVENTORY_MANAGEMENT.SET_PRODUCT_OWNERSHIP, inventoryManagement.setProductOwnerForTag);
	yield takeLatest(INVENTORY_MANAGEMENT.GET_RESTOCK_RECOMMENDATIONS, inventoryManagement.getRestockRecommendations);
	yield takeLatest(INVENTORY_MANAGEMENT.REFRESH_PAR, inventoryManagement.refreshPar);
	yield takeLatest(
		INVENTORY_MANAGEMENT.BULK_UPDATE_PRODUCT_TAG_SERIALS,
		inventoryManagement.bulkUpdateProductTagSerials,
	);
	yield takeLatest(INVENTORY_MANAGEMENT.BULK_FORCE_VEND, inventoryManagement.bulkForceVend);
	yield takeLatest(INVENTORY_MANAGEMENT.BULK_REVERT_PRODUCT_TAG_SERIALS, inventoryManagement.bulkRevertTag);
	yield takeLatest(
		INVENTORY_MANAGEMENT.BULK_RETURN_PRODUCT_TAG_SERIALS,
		inventoryManagement.bulkReturnProductTagSerialsRecord,
	);

	yield takeLatest(USER.RECORD_REQUEST, user.getUserRecord);

	yield takeLatest(USER_MANAGEMENT.LIST_REQUEST, userManagement.getUserList);
	yield takeLatest(USER_MANAGEMENT.CREATE_REQUEST, userManagement.createUser);
	yield takeLatest(USER_MANAGEMENT.UPDATE_REQUEST, userManagement.updateUser);
	yield takeLatest(USER_MANAGEMENT.USER_EXISTS_REQUEST, userManagement.checkIfUserExists);
	yield takeLatest(USER_MANAGEMENT.IMPORT_USERS, userManagement.importUsers);
	yield takeLatest(USER_MANAGEMENT.GET_VIMEO_APP_AUTH_TOKEN, userManagement.getVimeoAppAuthToken);
	yield takeLatest(USER_MANAGEMENT.SEND_SUPPORT_REQUEST, userManagement.sendSupportRequest);
	yield takeLatest(USER_MANAGEMENT.GET_SINGLE_USER, userManagement.getSingleUser);
	yield takeLatest(USER_MANAGEMENT.RESEND_INVITE_EMAIL, userManagement.resendInviteEmail);
	yield takeLatest(USER_MANAGEMENT.SET_PASSWORD, userManagement.setPassword);
	yield takeLatest(USER_MANAGEMENT.DELETE_USER, userManagement.deleteUser);
	yield takeLatest(USER_MANAGEMENT.INVOICE_LINK_LIST_REQUEST, userManagement.getInvoiceLinkUserList);
	yield takeLatest(USER_MANAGEMENT.CREATE_INVOICE_LINK_USER, userManagement.createInvoiceLinkUser);
	yield takeLatest(USER_MANAGEMENT.DELETE_INVOICE_LINK_USER, userManagement.deleteInvoiceLinkUser);

	yield takeLatest(USER.UPDATE_PERSONA_REQUEST, user.updatePersona);
	yield takeLatest(USER.CREATE_PERSONA_REQUEST, user.createPersona);

	yield takeLatest(USER.CREATE_REPORT_SUBSCRIPTION_REQUEST, user.createReportSubscription);
	yield takeLatest(USER.DELETE_REPORT_SUBSCRIPTION_REQUEST, user.deleteReportSubscription);
	yield takeLatest(USER.EDIT_REPORT_SUBSCRIPTION_REQUEST, user.editReportSubscription);
	yield takeLatest(USER.SEND_ONE_TIME_REPORT_REQUEST, user.sendOneTimeReportRequest);

	yield takeLatest(TENANTS.DETAILS.DETAIL_LIST, tenants.getTenantsDetails);

	yield takeLatest(TENANTS.CLEAR_TENANTS_LIST, tenants.resetTenantsList);
	yield takeLatest(TENANTS.DETAILS.BASIC_INFO_REQUEST, tenants.getTenantBasicInfo);

	yield takeLatest(CABINET.STATE.LIST, cabinets.getCabinetsStateList);
	yield takeLatest(TENANTS.DETAILS.ADDITIONAL_INFO_REQUEST, tenants.getTenantAdditionalInfo);

	yield takeLatest(ALERTS.LIST_REQUEST, alerts.getAlerts);
	yield takeLatest(ALERTS.GET_NOTIFICATION_TYPES, alerts.getNotificationTypes);
	yield takeLatest(ALERTS.GET_CHANNEL_TYPES, alerts.getChannelTypes);
	yield takeLatest(ALERTS.GET_NOTIFICATION_CHANNEL_GROUPS, alerts.getChannelGroups);
	yield takeLatest(ALERTS.SUBMIT_CHANNEL_GROUP, alerts.submitChannelGroup);
	yield takeLatest(ALERTS.DELETE_CHANNEL_GROUP, alerts.deleteChannelGroup);
	yield takeLatest(ALERTS.GET_INCIDENTS, alerts.getIncidents);
	yield takeLatest(ALERTS.GET_INCIDENT_HISTORY, alerts.getIncidentHistory);
	yield takeLatest(ALERTS.GET_INCIDENT_RULES, alerts.getIncidentRules);
	yield takeLatest(ALERTS.UPDATE_INCIDENT, alerts.updateIncident);

	yield takeLatest(TENANTS.GET_TENANT, tenants.getTenant);
	yield takeLatest(TENANTS.SYNC, tenants.syncCustomer);
	yield takeLatest(TENANTS.UPDATE_TENANT, tenants.updateCustomer);
	yield takeLatest(TENANTS.DETAILS.GET_PRODUCTS, tenants.getCustomerProducts);

	yield takeLatest(REPORTS.INVENTORY.CURRENT.FIX_COLUMNS, reports.fixTableColumn);
	yield takeLatest(REPORTS.INVENTORY.GROUP.REPORT_FILTER, reports.setFilter);
	yield takeLatest(REPORTS.DISPENSED.GET, reports.getDispensedProducts);
	yield takeLatest(REPORTS.CYCLE_COUNTS.GET, reports.getCycleCounts);
	yield takeLatest(REPORTS.SHIPMENTS.GET, reports.getWarehouseShipments);

	yield takeLatest(CABINET.GET_TELEMETRY_METRICS, cabinets.getTelemetryMetrics);
	yield takeLatest(CABINET.TEMPERATURE.GET_TEMPERATURE, cabinets.getTemperature);
	yield takeLatest(CABINET.TEMPERATURE.GET_ZONE_AVERAGE_TEMPERATURE, cabinets.getTemperature);
	yield takeLatest(CABINET.TEMPERATURE.GET_TEMPERATURE_CSV, cabinets.getTemperatureCsv);
	yield takeLatest(PRODUCTS.LIST_REQUEST, product.getProductsList);
	yield takeLatest(PRODUCTS.LIST_AUTOCOMPLETE_REQUEST, product.getAutoCompleteProducts);
	yield takeLatest(PRODUCTS.SAVE_NEW_PRODUCT, product.saveNewProduct);
	yield takeLatest(PRODUCTS.UPDATE_PRODUCT, product.updateProduct);

	yield takeLatest(ORDERS.LIST_REQUEST, orders.getOrdersList);
	yield takeLatest(ORDERS.GET_ORDER_REQUESTS, orders.getOrderRequests);
	yield takeLatest(ORDERS.GROUPED_LIST_REQUEST, orders.getGroupedOrdersList);
	yield takeLatest(ORDERS.GET_SHIPMENTS, orders.getShipments);
	yield takeLatest(ORDERS.GET_RECOMMENDATIONS, orders.getRecommendations);
	yield takeLatest(ORDERS.UPDATE_RESTOCK_ORDER_STATUS, orders.updateRestockRequestStatus);
	yield takeLatest(ORDERS.GET_CUSTOMER_PRODUCTS_FOR_ORDER, orders.getCustomerProductsForOrder);
	yield takeLatest(ORDERS.RELEASE_ORDERS, orders.releaseOrders);
	yield takeLatest(ORDERS.SYNC_ORDER_EXCLUSIONS, orders.syncOrderExclusions);
	yield takeLatest(ORDERS.GET_PAR_RECORD, orders.getParRecord);
	yield takeLatest(ORDERS.UPDATE_ORDER, orders.updateOrder);
	yield takeLatest(ORDERS.CHECK_JOB_STATUS, orders.checkOrderJobStatus);
	yield takeLatest(ORDERS.SYNC_WAREHOUSE_INVENTORY, orders.syncWarehouseInventory);
	yield takeLatest(ORDERS.SUBMIT_ORDER_REQUEST, orders.submitOrderRequest);
	yield takeLatest(ORDERS.UPDATE_ORDER_REQUESTS, orders.updateOrderRequests);
	yield takeLatest(ORDERS.DELETE_ORDER_REQUEST, orders.deleteOrderRequest);

	yield takeLatest(USER.CREATE_USER_PROPERTY_REQUEST, user.createUserProperty);
	yield takeLatest(USER.GET_USER_PROPERTY_REQUEST, user.getUserProperty);

	yield takeLatest(USER.SEND_USER_SETUP, user.setupUser);

	yield takeLatest(AUDIT.AUDIT_LOG_LIST_REQUEST, audit.getAuditLogList);

	yield takeLatest(USER.GET_USER_REPORT_QUEUE, user.getUserReportQueue);
	yield takeLatest(USER.UPDATE_USER_QUEUE_ITEM, user.updateUserQueueItem);

	yield takeLatest(USER.HANDLE_RNI_AUTH_TOKEN, user.handleRniAuthToken);
	yield takeLatest(USER.HANDLE_RNI_AUTH_PAGE_REFRESH, user.handleRniAuthPageRefresh);

	yield takeLatest(DASHBOARD.GET_ALL_DASHBOARD_VALUES, dashboard.getDashboardValues);
}
